import { Button, Modal, Popover, notification } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { DynamicForm, FormInput, FormSelect } from "shared/ui";
import { handleRequestError } from "shared/helpers";
import {
  useCreateMaterialCategoryMutation,
  UnitItemWithLabel,
} from "shared/api";
import { CloseOutlined } from "@ant-design/icons";
import {
  ModalSizeValue,
  lockMaterialCategoryValues,
  variantMaterialSelectValues,
} from "shared/constants";
import { AddOrEditMateriaCategoryValues } from "shared/types";

interface AddMaterialModalProps {
  isVisibleAddMaterialCategoryModal: boolean;
  hideAddMaterialCategoryModal: () => void;
  adaptedDimensionUnits: UnitItemWithLabel[];
}

const AddMaterialCategoryModal: FC<AddMaterialModalProps> = ({
  isVisibleAddMaterialCategoryModal,
  hideAddMaterialCategoryModal,
  adaptedDimensionUnits,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const [createMaterialCategoryRequest] = useCreateMaterialCategoryMutation();

  const {
    values,
    resetForm,
    isValid,
    handleBlur,
    handleChange,
    errors,
    setFieldValue,
  } = useFormikContext<AddOrEditMateriaCategoryValues>();

  const closeModalAndResetForm = () => {
    hideAddMaterialCategoryModal();
    resetForm();
  };

  const createMaterialCategory = async () => {
    try {
      await createMaterialCategoryRequest({
        name: values.name!,
        unit: values.unit!,
        unique_code: values.unique_code!,
        config_dynamic_tracker_fields: values.sections || [],
        lock_material_consume_variant: values?.lock_material_consume_variant!,
        // We don't set supplier in this modal. But it's required in the API
        suppliers: [],
        default_material_consume_variant:
          values?.default_material_consume_variant!,
        export_col_for_quantity: values.export_col_for_quantity
          ? values.export_col_for_quantity
          : null,
        export_col_for_serial_number: values.export_col_for_serial_number
          ? values.export_col_for_serial_number
          : null,
      }).unwrap();
      api.success({
        message: t("material-category-management.success-title"),
        description: t("material-category-management.success-add-desc"),
      });

      closeModalAndResetForm();
    } catch (error) {
      const description = handleRequestError(
        error,
        t("material-category-management.error-title")
      );

      api.error({
        message: t("material-category-management.error-title"),
        description,
      });
    }
  };

  const clearDefaultMaterialConsumeVariant = () => {
    setFieldValue("default_material_consume_variant", null);
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        title={t("material-category-management.add-modal-name")}
        open={isVisibleAddMaterialCategoryModal}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${themeColor}`,
          },
        }}
        width={
          values?.sections?.length
            ? ModalSizeValue.LARGE
            : ModalSizeValue.SECONDARY
        }
        className="p-4"
        onCancel={closeModalAndResetForm}
        onOk={createMaterialCategory}
      >
        <Title level={5} className="mb-1.25">
          {t("material-category-management.add-modal-title")}
        </Title>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t(
            "material-category-management.add-modal-name-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!errors.name}
        />

        <Title level={5} className="mb-1.25">
          {t("material-category-management.unique-code")}
        </Title>

        <FormInput
          value={values.unique_code}
          fieldName="unique_code"
          placeholder={t(
            "material-category-management.unique-code-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.unique_code}
          isError={!!errors.unique_code}
        />

        <Title level={5} className="mb-1.25">
          {t("material-category-management.export-col-for-serial-number")}
        </Title>

        <FormInput
          value={values.export_col_for_serial_number}
          fieldName="export_col_for_serial_number"
          placeholder={t(
            "material-category-management.export-col-for-serial-number-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.export_col_for_serial_number}
          isError={!!errors.export_col_for_serial_number}
          numberType
        />

        <Title level={5} className="mb-1.25">
          {t("material-category-management.export-col-for-quantity")}
        </Title>

        <FormInput
          value={values.export_col_for_quantity}
          fieldName="export_col_for_quantity"
          placeholder={t(
            "material-category-management.export-col-for-quantity-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.export_col_for_quantity}
          isError={!!errors.export_col_for_quantity}
          numberType
        />

        <div className="w-full flex items-center justify-between">
          <Title level={5} className="mb-1.25">
            {t("material-category-management.default-material-consume-variant")}
          </Title>

          <Popover content={t("material-category-management.clear-values")}>
            <Button
              className="w-6 h-6 rounded-full p-0 flex items-center justify-center mb-2"
              onClick={clearDefaultMaterialConsumeVariant}
            >
              <CloseOutlined />
            </Button>
          </Popover>
        </div>

        <FormSelect
          value={values?.default_material_consume_variant}
          placeholder={t(
            "material-category-management.default-material-consume-variant"
          )}
          fieldName="default_material_consume_variant"
          options={variantMaterialSelectValues}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors?.default_material_consume_variant}
          isError={!!errors.default_material_consume_variant}
        />

        <Title level={5} className="mb-1.25">
          {t("material-category-management.lock-material-consume-variant")}
        </Title>

        <FormSelect
          value={values?.lock_material_consume_variant}
          placeholder={t(
            "material-category-management.lock-material-consume-variant"
          )}
          fieldName="lock_material_consume_variant"
          options={lockMaterialCategoryValues}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors?.lock_material_consume_variant}
          isError={!!errors.lock_material_consume_variant}
        />

        <Title level={5} className="mb-1.25">
          {t("material-category-management.add-modal-unit")}
        </Title>

        <FormSelect
          value={values?.unit}
          placeholder={t(
            "material-category-management.add-modal-unit-placeholder"
          )}
          fieldName="unit"
          withLabels
          multiple={false}
          optionsWithLabels={adaptedDimensionUnits}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors?.unit}
          isError={!!errors.unit}
        />

        <DynamicForm adaptedDimensionUnits={adaptedDimensionUnits} />
      </Modal>
    </>
  );
};

export { AddMaterialCategoryModal };
